import React from 'react';
import styled from '@emotion/styled';
import { Button, Colors, Spinner, TextInput } from '@zapier/design-system';
import { mediaQueries } from 'components/mediaQueries';
import { useTrackEvent } from 'tracking/useTrackEvent';
import { useSubscribeEmail } from 'hooks/useSubscribeEmail';
import {
  ErrorMessage,
  SuccessMessage,
} from 'components/Subscribe/FlashMessages';

export const SubscribePopup = () => {
  const trackEvent = useTrackEvent();
  const { error, loading, success, submitEmail } = useSubscribeEmail();
  const [firedAlready, setFiredAlready] = React.useState<boolean>(false);
  const [email, setEmail] = React.useState<string>('');

  React.useEffect(() => {
    if (success) {
      if (!firedAlready) {
        trackEvent('call_to_action', {
          path: window.location.pathname,
          from_path: document.referrer,
          cta_name: 'email_subscribe_confirm',
          cta_raw_text: 'email address submitted',
          cta_location: 'popup',
          cta_goal: 'email_subscribe',
          link_destination: '',
          signup_type: '',
        });
        setFiredAlready(true);
      }
    }
  }, [success]);

  if (loading) {
    return <Spinner />;
  }

  if (error) {
    return (
      <ErrorMessage>Oops! We had trouble subscribing this email.</ErrorMessage>
    );
  }

  if (success) {
    return (
      <SuccessMessage>Excellent! You have been subscribed.</SuccessMessage>
    );
  }

  return (
    <Outer>
      <Heading>Never miss a post</Heading>
      <Details>
        We’ll email you 1-3 times per week—and never share your information.
      </Details>
      <Form
        onSubmit={(e: React.SyntheticEvent) => {
          e.preventDefault();
          submitEmail(email);
        }}
      >
        <TextInputWrapper>
          <TextInput
            ariaLabel="Email address"
            color="grey100"
            isRequired={true}
            onChange={(e: React.SyntheticEvent) => {
              const value = (e.target as HTMLInputElement).value;
              setEmail(value);
            }}
            onFocus={() =>
              trackEvent('call_to_action', {
                path: window.location.pathname,
                from_path: document.referrer,
                cta_name: 'email_subscribe_intent',
                cta_raw_text: 'never miss a post',
                cta_location: 'popup',
                cta_goal: 'email_subscribe',
                link_destination: '',
                signup_type: '',
              })
            }
            placeholder="Email address"
            size="small"
            type="email"
          />
        </TextInputWrapper>
        <Button hasShadow={true} size="small" type="submit">
          Subscribe
        </Button>
      </Form>
    </Outer>
  );
};

const Outer = styled.div`
  padding: 0 5%;
  display: grid;
  grid-row-gap: 10px;
  place-content: center;
  ${mediaQueries.tablet} {
    grid-column-gap: 15px;
    grid-template-columns: repeat(2, max-content);
    place-content: center;
    place-items: center;
  }
  ${mediaQueries.desktop} {
    grid-template-columns: 1fr;
    padding: 0;
    place-content: baseline;
    place-items: baseline;
  }
`;

const Heading = styled.p`
  color: ${Colors.grey900};
  font-weight: 800;
  font-size: 16px;
  line-height: 150%;
`;

const Details = styled.p`
  color: ${Colors.grey700};
  display: none;
  font-size: 11px;
  line-height: 130%;
  ${mediaQueries.desktop} {
    display: block;
  }
`;

const TextInputWrapper = styled.div`
  ${mediaQueries.desktop} {
    width: 130px;
  }
`;

const Form = styled.form`
  display: grid;
  grid-column-gap: 9px;
  grid-template-columns: repeat(2, 1fr);
`;
